import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Eye,
  Share2,
  Bookmark,
  Play,
  Star,
  Zap,
  Plus,
  Filter,
} from "lucide-react";
import { Link } from "react-router-dom";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import PageLayout from "../components/ui/PageLayout";

// Mock data étendu pour le contenu
const generateMockContent = (startId = 1, count = 20) => {
  const images = [
    "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400",
    "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400",
    "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400",
    "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400",
    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400",
    "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=400",
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400",
    "https://images.unsplash.com/photo-1520637836862-4d197d17c33a?w=400",
  ];

  const titles = [
    "Neon City Dreams",
    "Mystical Forest Path",
    "Abstract Flow",
    "Dragon's Realm",
    "Portrait Study",
    "Space Explorer",
    "Digital Sunset",
    "Cosmic Dance",
    "Urban Legends",
    "Ethereal Beauty",
    "Future Visions",
    "Ancient Wisdom",
    "Color Symphony",
    "Light & Shadow",
    "Digital Renaissance",
    "Quantum Art",
  ];

  const creators = [
    {
      name: "Alex Chen",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100",
    },
    {
      name: "Sarah Wilson",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100",
    },
    {
      name: "Mike Rodriguez",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    },
    {
      name: "Emma Thompson",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    },
  ];

  return Array.from({ length: count }, (_, index) => {
    const id = startId + index;
    return {
      id,
      title: titles[index % titles.length],
      creator: creators[index % creators.length],
      image: images[index % images.length],
      likes: Math.floor(Math.random() * 5000) + 100,
      views: Math.floor(Math.random() * 15000) + 500,
      isLiked: Math.random() > 0.7,
      isBookmarked: Math.random() > 0.8,
      type: Math.random() > 0.8 ? "video" : "image",
      featured: Math.random() > 0.9,
      published: Math.random() > 0.5, // New published property
      category: ["Art", "Abstract", "Nature", "Futuristic", "Portrait"][
        Math.floor(Math.random() * 5)
      ],
      createdAt: new Date(
        Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
    };
  });
};

const PublishedContentPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [content, setContent] = useState(() =>
    generateMockContent(1, 20).filter((item) => item.published)
  );
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleLike = (id: any) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              isLiked: !item.isLiked,
              likes: item.isLiked ? item.likes - 1 : item.likes + 1,
            }
          : item
      )
    );
  };

  const handleBookmark = (id: any) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isBookmarked: !item.isBookmarked } : item
      )
    );
  };

  const loadMore = () => {
    if (loading) return;

    setLoading(true);
    setTimeout(() => {
      const newContent = generateMockContent(content.length + 1, 10).filter(
        (item) => item.published
      );
      setContent((prev) => [...prev, ...newContent]);
      setLoading(false);

      if (content.length > 100) {
        setHasMore(false);
      }
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white">
      <PageLayout>
        <motion.section
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg font-bold text-white flex items-center gap-3">
                <Zap className="text-purple-500" />
              Published Content
            </h2>
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filters
              </motion.button>
            </div>
          </div>

          {/* Masonry Grid */}
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
            {content.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 1) }}
                className="break-inside-avoid group cursor-pointer"
              >
                <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/30 hover:border-purple-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                  <div className="relative">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{
                        height: `${Math.floor(250 + (index % 4) * 80)}px`,
                      }}
                    />

                    {item.type === "video" && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="bg-black/60 backdrop-blur-sm rounded-full p-4 hover:bg-black/80 transition-colors"
                        >
                          <Play className="w-8 h-8 text-white" />
                        </motion.div>
                      </div>
                    )}

                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike(item.id);
                          }}
                          className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
                            item.isLiked
                              ? "bg-red-500/80 text-white"
                              : "bg-black/50 text-gray-300 hover:bg-red-500/50"
                          }`}
                        >
                          <Heart
                            className={`w-4 h-4 ${item.isLiked ? "fill-current" : ""}`}
                          />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBookmark(item.id);
                          }}
                          className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
                            item.isBookmarked
                              ? "bg-blue-500/80 text-white"
                              : "bg-black/50 text-gray-300 hover:bg-blue-500/50"
                          }`}
                        >
                          <Bookmark
                            className={`w-4 h-4 ${item.isBookmarked ? "fill-current" : ""}`}
                          />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 rounded-full backdrop-blur-sm bg-black/50 text-gray-300 hover:bg-gray-600/50 transition-colors"
                        >
                          <Share2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="text-white font-semibold text-lg mb-3 line-clamp-2 leading-tight">
                      {item.title}
                    </h3>

                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <img
                          src={item.creator.avatar}
                          alt={item.creator.name}
                          className="w-6 h-6 rounded-full ring-1 ring-gray-600"
                        />
                        <span className="text-gray-300 text-sm font-medium">
                          {item.creator.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-700/50 px-2 py-1 rounded-full">
                        {item.category}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1 hover:text-red-400 transition-colors">
                          <Heart className="w-4 h-4" />
                          <span>{item.likes.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center space-x-1 hover:text-blue-400 transition-colors">
                          <Eye className="w-4 h-4" />
                          <span>{item.views.toLocaleString()}</span>
                        </div>
                      </div>
                      <span className="text-xs">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={loadMore}
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3"
              >
                {loading ? (
                  <LoadingIndicator />
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Load More
                  </>
                )}
              </motion.button>
            </div>
          )}

          {!hasMore && content.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-6xl mb-4">🎨</div>
              <h3 className="text-2xl font-bold text-white mb-2">
                You've seen it all!
              </h3>
              <p className="text-gray-400 text-lg">
                Amazing work exploring the community's creativity.
              </p>
            </motion.div>
          )}
        </div>
        </motion.section>
      </PageLayout>
    </div>
  );
};

export default PublishedContentPage;
